import React, { useEffect, useMemo } from "react";
import { Stack, Box, Chip } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createSubject, updateSubject } from "../redux/actions/subjectActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { subjectActions } from "../redux/slices/subject";
import { cleanFormData } from "../helpers/formHelpers";

import { SubjectResponseDto } from "../shared/dtos/subject/SubjectResponseDto";
import { SubjectRequestDto } from "../shared/dtos/subject/SubjectRequestDto";

const subjectSchema = Yup.object().shape({
  code: Yup.string().required("Subject code is required (e.g., MAT101)"),
  name: Yup.string().required("Subject name is required"),
  description: Yup.string().nullable(),
  applicableGradeLevelIds: Yup.array()
    .of(Yup.string().required())
    .min(1, "Select at least one grade level")
    .required("Grade levels are required"),
});

interface CreateOrUpdateSubjectFormProps {
  subject?: SubjectResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateSubjectForm: React.FC<CreateOrUpdateSubjectFormProps> = ({
  subject = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  // Redux: Grade Levels for the multi-select dropdown
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);

  useEffect(() => {
    if (!gradeLevels || gradeLevels.length === 0) {
      dispatch(getGradeLevels());
    }
  }, [dispatch, gradeLevels]);

  const gradeOptions = useMemo(
    () => gradeLevels?.map((g) => ({ value: g.id, label: g.name })) || [],
    [gradeLevels],
  );

  // Determine initial selected grade level IDs
  const getInitialGradeIds = (): string[] => {
    if (!subject) return [];

    // Map names to IDs if only names are present in the response DTO
    if (subject.applicableGradeLevelNames && gradeLevels) {
      return gradeLevels
        .filter((gl) => subject.applicableGradeLevelNames?.includes(gl.name))
        .map((gl) => gl.id);
    }
    return [];
  };

  const initialValues: SubjectRequestDto = {
    code: subject?.code || "",
    name: subject?.name || "",
    description: subject?.description || "",
    applicableGradeLevelIds: getInitialGradeIds(),
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(subjectActions.setErrorUpdate(null))
            : () => dispatch(subjectActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={subjectSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: SubjectRequestDto = {
            code: rawCleaned.code,
            name: rawCleaned.name,
            description: rawCleaned.description || null,
            applicableGradeLevelIds: Array.isArray(
              values.applicableGradeLevelIds,
            )
              ? values.applicableGradeLevelIds
              : [],
          };

          if (isEditing && subject) {
            dispatch(updateSubject({ id: subject.id, values: cleanedValues }));
          } else {
            dispatch(createSubject(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          dirty,
          isValid,
          setFieldValue,
        }: FormikProps<SubjectRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="code"
                  label="Subject Code"
                  placeholder="e.g., SCI101"
                />
                <FormTextField
                  name="name"
                  label="Subject Name"
                  placeholder="e.g., General Science"
                />
              </Box>

              <FormTextField
                name="description"
                label="Description"
                multiline
                rows={3}
              />

              {/* Multi-Select Dropdown for Grade Levels */}
              <FormSelectField
                name="applicableGradeLevelIds"
                label="Applicable Grade Levels"
                options={gradeOptions}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((val) => {
                      const option = gradeOptions.find((o) => o.value === val);

                      return (
                        <Chip
                          key={val}
                          label={option ? option.label : val}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Subject"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateSubjectForm;
